import { collectConditionFields, detectConditionCycles, indexFields } from './conditions.js';
import type { Condition, ConditionGroup, FieldDef, TemplateDefinition } from './types.js';
import { FIELD_TYPES } from './types.js';
import { DEFAULT_LIMITS } from './values.js';
import { defaultValueProblem } from './defaults.js';

export interface PublishIssue {
  code: string;
  message: string;
  path?: string;
  severity: 'error' | 'warning';
}

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const KEY_RE = /^[a-z][a-z0-9_]{0,63}$/;
const NUMERIC_TYPES = new Set(['integer', 'decimal', 'currency', 'year']);
const SELECT_TYPES = new Set(['single_select', 'multi_select']);

function walkConditions(g: ConditionGroup | undefined, fn: (c: Condition) => void) {
  if (!g) return;
  for (const x of [...(g.all ?? []), ...(g.any ?? [])]) {
    if ('all' in x || 'any' in x) walkConditions(x as ConditionGroup, fn);
    else fn(x as Condition);
  }
}

export function validateTemplateForPublish(
  t: TemplateDefinition,
  previous?: TemplateDefinition | null,
): PublishIssue[] {
  const issues: PublishIssue[] = [];
  const err = (code: string, message: string, path?: string) => issues.push({ code, message, path, severity: 'error' });
  const warn = (code: string, message: string, path?: string) => issues.push({ code, message, path, severity: 'warning' });

  if (!t.name?.trim()) err('template.name', 'Template name is required.');
  if (!KEY_RE.test(t.key ?? '')) err('template.key', 'Template key must be lowercase snake_case.');
  if (!t.sections?.length) err('template.sections', 'Add at least one section.');

  const sectionIds = new Set<string>();
  const fieldIds = new Set<string>();
  const fieldKeys = new Set<string>();

  const checkField = (f: FieldDef, path: string, isChild: boolean) => {
    if (!ID_RE.test(f.id ?? '')) err('field.id', `Invalid field id “${f.id}”.`, path);
    if (fieldIds.has(f.id)) err('field.id_duplicate', `Duplicate field id “${f.id}”.`, path);
    fieldIds.add(f.id);
    if (!KEY_RE.test(f.key ?? '')) err('field.key', `Invalid field key “${f.key}”.`, path);
    else if (!isChild && fieldKeys.has(f.key)) err('field.key_duplicate', `Duplicate field key “${f.key}”.`, path);
    if (!isChild) fieldKeys.add(f.key);
    if (!(FIELD_TYPES as readonly string[]).includes(f.type)) err('field.type', `Unknown field type “${f.type}”.`, path);
    if (!f.label?.trim()) err('field.label', 'Field label is required.', path);
    if (f.type === 'info' && (f.required || f.requiredWhen)) err('field.info_required', 'Instruction blocks cannot be required.', path);
    if (SELECT_TYPES.has(f.type)) {
      const opts = f.options ?? [];
      if (opts.filter((o) => !o.retired).length === 0) err('field.options', `“${f.label}” needs at least one option.`, path);
      const oids = new Set<string>();
      const okeys = new Set<string>();
      for (const o of opts) {
        if (!ID_RE.test(o.id ?? '')) err('option.id', `Invalid option id “${o.id}”.`, path);
        if (oids.has(o.id)) err('option.id_duplicate', `Duplicate option id “${o.id}” in “${f.label}”.`, path);
        if (okeys.has(o.key)) err('option.key_duplicate', `Duplicate option key “${o.key}” in “${f.label}”.`, path);
        oids.add(o.id);
        okeys.add(o.key);
        if (!o.label?.trim()) err('option.label', 'Option label is required.', path);
      }
      if (f.type === 'single_select' && opts.some((o) => o.exclusive))
        warn('option.exclusive_single', 'Exclusive options have no effect on single-select fields.', path);
    } else if (f.options?.length) {
      err('field.options_unexpected', `Options are only valid on select fields (“${f.label}”).`, path);
    }
    if (f.type === 'repeater') {
      if (isChild) err('field.nested_repeater', 'Repeaters cannot be nested.', path);
      if (!f.children?.length) err('field.children', `Repeater “${f.label}” needs at least one child field.`, path);
    } else if (f.children?.length) {
      err('field.children_unexpected', 'Only repeaters may have child fields.', path);
    }
    const v = f.validation;
    if (v?.maxLength !== undefined) {
      const hard = f.type === 'short_text' ? DEFAULT_LIMITS.shortTextHardMax : DEFAULT_LIMITS.longTextHardMax;
      if (v.maxLength < 1 || v.maxLength > hard) err('field.max_length', `Max length must be between 1 and ${hard}.`, path);
    }
    if (v?.min !== undefined && v?.max !== undefined && v.min > v.max) err('field.min_max', 'Minimum exceeds maximum.', path);
    if (v?.maxRows !== undefined && v.maxRows > DEFAULT_LIMITS.repeaterHardMaxRows)
      err('field.max_rows', `Repeaters are limited to ${DEFAULT_LIMITS.repeaterHardMaxRows} rows.`, path);
    if ((f.type === 'acknowledgement' || f.type === 'info') && !f.text?.trim() && !f.label?.trim())
      err('field.text', 'Acknowledgement text is required.', path);
    const defaultProblem = defaultValueProblem(f, isChild);
    if (defaultProblem) err('field.default_value', `“${f.label}”: ${defaultProblem}`, path);
  };

  t.sections?.forEach((s, si) => {
    const sp = `sections[${si}]`;
    if (!ID_RE.test(s.id ?? '')) err('section.id', `Invalid section id “${s.id}”.`, sp);
    if (sectionIds.has(s.id)) err('section.id_duplicate', `Duplicate section id “${s.id}”.`, sp);
    sectionIds.add(s.id);
    if (!s.title?.trim()) err('section.title', 'Section title is required.', sp);
    if (!s.fields?.length) warn('section.empty', `Section “${s.title}” has no fields.`, sp);
    s.fields?.forEach((f, fi) => {
      checkField(f, `${sp}.fields[${fi}]`, false);
      f.children?.forEach((c, ci) => checkField(c, `${sp}.fields[${fi}].children[${ci}]`, true));
    });
  });

  // Reference checks
  const idx = indexFields(t);
  const checkRefs = (g: ConditionGroup | undefined, path: string, ownerId: string | null, parent?: FieldDef) => {
    walkConditions(g, (c) => {
      const ref = idx.get(c.field);
      if (!ref) return err('condition.reference', `Condition references missing field “${c.field}”.`, path);
      if (ownerId && c.field === ownerId) err('condition.self', 'A field cannot depend on itself.', path);
      if (ref.parent && ref.parent.id !== parent?.id)
        err('condition.row_scope', `“${ref.field.label}” is a repeater child and can only be referenced inside its own row.`, path);
      const rf = ref.field;
      if (['gt', 'gte', 'lt', 'lte'].includes(c.op) && !NUMERIC_TYPES.has(rf.type))
        err('condition.numeric', `Numeric comparison on non-numeric field “${rf.label}”.`, path);
      if (['gt', 'gte', 'lt', 'lte'].includes(c.op) && typeof c.value !== 'number')
        err('condition.value', 'Numeric comparisons need a number value.', path);
      if (SELECT_TYPES.has(rf.type) && ['equals', 'not_equals', 'contains_any'].includes(c.op)) {
        const vals = Array.isArray(c.value) ? c.value : [c.value];
        for (const val of vals) {
          if (!rf.options?.some((o) => o.id === val))
            err('condition.option', `Condition on “${rf.label}” references unknown option “${String(val)}”.`, path);
        }
      }
      if (rf.type === 'info' || rf.type === 'repeater') err('condition.target', `Cannot build a condition on “${rf.label}”.`, path);
    });
  };
  t.sections?.forEach((s, si) => {
    checkRefs(s.visibleWhen, `sections[${si}].visibleWhen`, null);
    s.fields?.forEach((f, fi) => {
      checkRefs(f.visibleWhen, `sections[${si}].fields[${fi}].visibleWhen`, f.id);
      checkRefs(f.requiredWhen, `sections[${si}].fields[${fi}].requiredWhen`, f.id);
      f.children?.forEach((c, ci) => {
        checkRefs(c.visibleWhen, `sections[${si}].fields[${fi}].children[${ci}].visibleWhen`, c.id, f);
        checkRefs(c.requiredWhen, `sections[${si}].fields[${fi}].children[${ci}].requiredWhen`, c.id, f);
      });
    });
  });

  for (const cycle of detectConditionCycles(t)) {
    err('condition.cycle', `Conditional rules form a cycle: ${cycle.join(' → ')}.`);
  }

  // A field required but only visible when referencing itself etc. is covered above; flag required
  // fields that depend on a field in a later hidden section as warnings.
  for (const s of t.sections ?? []) {
    for (const f of s.fields ?? []) {
      for (const dep of collectConditionFields(f.visibleWhen)) {
        const d = idx.get(dep);
        if (d && d.field.audience === 'internal' && f.audience !== 'internal')
          warn('condition.internal_dependency', `Client field “${f.label}” depends on internal field “${d.field.label}”.`);
      }
    }
  }

  // Bi-weekly semantics must be clarified before publication.
  const hasBiweekly = [...idx.values()].some((e) => e.field.options?.some((o) => o.key === 'biweekly'));
  if (hasBiweekly && !t.settings?.biweeklyMeaning)
    err('settings.biweekly', 'Clarify whether “Bi-weekly” means every two weeks or twice a month before publishing.');

  if (t.confirmation) {
    if (!t.confirmation.text?.trim()) err('confirmation.text', 'Confirmation text is required.');
    if (!t.confirmation.textVersion?.trim()) err('confirmation.version', 'Confirmation text version is required.');
  }

  // Stable-ID integrity against the previous published version.
  if (previous) {
    const prevIdx = indexFields(previous);
    for (const [id, entry] of prevIdx) {
      const now = idx.get(id);
      if (!now) continue;
      if (now.field.type !== entry.field.type)
        err('stable.type_change', `Field id “${id}” changed type from ${entry.field.type} to ${now.field.type}. Create a new field instead.`);
      const prevOpts = new Map((entry.field.options ?? []).map((o) => [o.id, o]));
      for (const o of now.field.options ?? []) {
        const p = prevOpts.get(o.id);
        if (p && p.key !== o.key) err('stable.option_key', `Option id “${o.id}” changed meaning (${p.key} → ${o.key}).`);
      }
      for (const p of entry.field.options ?? []) {
        if (!now.field.options?.some((o) => o.id === p.id))
          warn('stable.option_removed', `Option “${p.label}” was removed from “${entry.field.label}”; retire it instead to preserve history.`);
      }
    }
  }

  return issues;
}

/** Placeholders in message templates must come from an allowlist. */
export function findDisallowedPlaceholders(text: string, allowlist: readonly string[]): string[] {
  const bad = new Set<string>();
  for (const m of text.matchAll(/\{\{\s*([^}]*?)\s*\}\}/g)) {
    const name = m[1] ?? '';
    if (!allowlist.includes(name)) bad.add(name);
  }
  return [...bad];
}
